import { tokenStorage } from "@/auth/storage";
/**
 * Platform telemetry client — backs the dashboard.
 *
 * - `fetchPlatformSummary()`: one-shot snapshot for the top stat strip
 *   and service panels.
 * - `streamPlatformEvents()`: SSE feed for the activity log. Reuses the
 *   SSE frame parser from the chat client because the BE emits the same
 *   wire format on both endpoints.
 */
import { parseSseFrames } from "@/chat/sseClient";

import { apiFetch } from "./client";
import type {
  ClusterStatus,
  PlatformEvent,
  PlatformSummary,
} from "./generated";

export function fetchPlatformSummary(): Promise<PlatformSummary> {
  return apiFetch<PlatformSummary>("/api/v1/platform/summary");
}

export function fetchClusterStatus(): Promise<ClusterStatus> {
  return apiFetch<ClusterStatus>("/api/v1/cluster/status");
}

/**
 * Recent CLI agent activity for the caller's tenant (newest first), as
 * `CliActivity` events. Used to seed the dashboard feed on load — the live
 * SSE stream only carries events that arrive while subscribed.
 */
export function fetchCliActivityHistory(
  limit = 20,
  signal?: AbortSignal,
): Promise<PlatformEvent[]> {
  return apiFetch<PlatformEvent[]>(
    `/api/v1/platform/cli-activity?limit=${limit}`,
    { signal },
  );
}

export interface StreamPlatformEventsOptions {
  signal?: AbortSignal;
  baseUrl?: string;
}

/**
 * Open the platform event stream and yield decoded `PlatformEvent`s.
 *
 * Throws on non-2xx response. The stream ends when the server closes
 * the connection or the caller aborts via `opts.signal`.
 */
export async function* streamPlatformEvents(
  opts: StreamPlatformEventsOptions = {},
): AsyncGenerator<PlatformEvent, void, void> {
  const baseUrl = opts.baseUrl ?? "";
  const headers: Record<string, string> = {
    Accept: "text/event-stream",
  };
  const tokens = tokenStorage.load();
  if (tokens?.access_token) {
    headers.Authorization = `Bearer ${tokens.access_token}`;
  }

  const res = await fetch(`${baseUrl}/api/v1/platform/events`, {
    method: "GET",
    headers,
    signal: opts.signal,
  });

  if (!res.ok) {
    throw new Error(`platform/events HTTP ${res.status} ${res.statusText}`);
  }
  if (!res.body) {
    throw new Error("platform/events response had no body");
  }

  for await (const frame of parseSseFrames(res.body)) {
    // The BE emits unnamed `data:`-only events (event default is
    // "message"). Drop anything else (forward-compat for future
    // typed events).
    if (frame.event !== "message" && frame.event !== "") continue;
    try {
      yield JSON.parse(frame.data) as PlatformEvent;
    } catch {
      // Skip unparseable frames; the connection itself is still healthy.
    }
  }
}

/** Most recent activity items kept in the dashboard table. */
export const ACTIVITY_BUFFER_LIMIT = 20;

export interface ActivityRow {
  /** Stable id within the buffer — used as the keyed list key. */
  id: string;
  /** RFC 3339 timestamp the BE attached to the source event. */
  at: string;
  /** UI source-bucket tag — drives the colored chip in the table. */
  source: "api" | "vm" | "chat" | "cli";
  /** Short human-readable event description. */
  message: string;
}

/**
 * Pure reducer: append a freshly-arrived event to the rolling buffer.
 *
 * Always returns a new array (immutable). Caps the buffer at
 * `ACTIVITY_BUFFER_LIMIT` to bound the DOM cost on long sessions.
 * Heartbeats are dropped — they're a stream-liveness probe, not an
 * activity item.
 */
export function appendActivity(
  buffer: ReadonlyArray<ActivityRow>,
  event: PlatformEvent,
): ActivityRow[] {
  const row = activityRowFromEvent(event);
  if (!row) return buffer.slice();
  // Dedup by id: an event seeded from history can also arrive over SSE in the
  // brief overlap window — keep the first, drop the echo.
  if (buffer.some((r) => r.id === row.id)) return buffer.slice();
  const next = [row, ...buffer];
  if (next.length > ACTIVITY_BUFFER_LIMIT) {
    next.length = ACTIVITY_BUFFER_LIMIT;
  }
  return next;
}

/**
 * Seed the buffer with historical events (newest-first, as returned by
 * `/api/v1/platform/cli-activity`). History is older than anything already
 * live in `buffer`, so it's placed *after* it; ids already present are skipped
 * so a live event and its history row collapse to one entry. Caps the buffer.
 */
export function mergeHistory(
  buffer: ReadonlyArray<ActivityRow>,
  events: ReadonlyArray<PlatformEvent>,
): ActivityRow[] {
  const seen = new Set(buffer.map((r) => r.id));
  const older: ActivityRow[] = [];
  for (const event of events) {
    const row = activityRowFromEvent(event);
    if (!row || seen.has(row.id)) continue;
    seen.add(row.id);
    older.push(row);
  }
  return [...buffer, ...older].slice(0, ACTIVITY_BUFFER_LIMIT);
}

function activityRowFromEvent(event: PlatformEvent): ActivityRow | null {
  if (event.kind === "heartbeat") return null;
  if (event.kind === "vm_lifecycle") {
    return {
      id: `vm:${event.vm_id}:${event.at}:${event.action}`,
      at: event.at,
      source: "vm",
      message: `vm ${event.vm_id.slice(0, 8)} ${event.action}`,
    };
  }
  if (event.kind === "chat_turn") {
    const latencySec = (event.latency_ms / 1000).toFixed(1);
    const usage =
      event.completion_tokens > 0
        ? `${event.completion_tokens} tok in ${latencySec}s`
        : `no usage reported · ${latencySec}s`;
    return {
      id: `chat:${event.at}:${event.model}`,
      at: event.at,
      source: "chat",
      message: `${event.model} · ${usage}`,
    };
  }
  if (event.kind === "cli_activity") {
    // Mission-control z01 CLI agent activity, pushed by the data-plane bridge.
    const who = event.agent ?? "agent";
    const verb = event.event_kind.replace(/_/g, " ");
    const task = event.task_id ? ` ${event.task_id.slice(-8)}` : "";
    const detail = event.summary ? ` · ${event.summary}` : "";
    return {
      // Source event id isn't on the wire, so key on host+task+kind+ts —
      // unique per forwarded event.
      id: `cli:${event.host}:${event.task_id ?? "-"}:${event.event_kind}:${event.at}`,
      at: event.at,
      source: "cli",
      message: `${who} ${verb}${task}${detail}`,
    };
  }
  return null;
}
